import type { ReactNode } from 'react';
import type { Slide } from '@/framework/types';
import { LoopDiagram, RuntimeDiagram, TimelineDiagram, VersionsDiagram, WorkflowDiagram, lit } from './diagrams';
import architecture from './images/agent-engine-architecture.svg';
import lifecycle from './images/adlc-flywheel.svg';
import custody from './images/credential-custody.svg';
import resourceModel from './images/resource-model.svg';
import {
  ApiLine,
  Call,
  Code,
  Diagram,
  EventLog,
  Figure,
  Frame,
  Head,
  Hi,
  Lockup,
  PageContext,
  Panel,
  Qr,
  ResourceNav,
  Steps,
  Table,
} from './parts';
import {
  AGENT_JSON,
  ASK_JSON,
  CONFIRM_CLI,
  CONNECT_CLI,
  CONNECT_PYTHON,
  CONNECT_TS,
  FILE_CLI,
  FOUR_CALLS_CLI,
  MCP_JSON,
  MEMORY_CLI,
  RUN_CLI,
  RUN_PYTHON,
  RUN_TS,
  SKILL_CLI,
  TRIGGER_CLI,
  TURN_PYTHON,
  TURN_TS,
  VAULT_CLI,
} from './samples';
import './orca.css';

/**
 * Orca 101, live: forty minutes ahead of the Data + Agent Hackathon tutorial,
 * for a room that has not met Orca before.
 *
 * The order is the one a newcomer needs: what Orca Agent Engine is, its
 * resource model and its API, each resource in turn, then how to build an
 * agent. No term is used before the slide that explains it, which is why the
 * walk through the internals ("one message, hop by hop") comes after the
 * resources and not before them.
 *
 * The build part follows the tutorial's labs. In the tutorial an attendee runs
 * one script per lab; the commands and JSON on slides 25 to 29 are what those
 * scripts send, and slide 23 says so before any of them appears.
 *
 * Sources per slide, and the claims this deck does not make, are in README.md.
 *
 * Editing a slide: this deck is Orca-branded. Its colours and type come from
 * the classes in orca.css, a documented exception to the repo's --sn-* tokens,
 * so keep to those classes rather than the StreamNative palette. Code samples
 * live in samples.ts, each with the source it has to keep matching.
 */

const OPEN = 'Open';
const WHAT = 'What it is';
const MODEL = 'Resources and the API';
const RESOURCE = 'Resource by resource';
const BUILD = 'Build an agent';
const NEXT = 'Into the tutorial';

/** Orca 101's own caption for a worked trace. */
const ILLUSTRATION = 'An illustration. Event types follow the API reference; the values are examples.';

/** The tag on the four resources the labs do not reach. */
const AFTER_LABS = 'For your project';

type Row = readonly ReactNode[];

/** A resource's or a lab's name in a table cell. */
const name = (text: string) => <span className="key">{text}</span>;

/** Slide 5: what a script leaves open, and the resource that answers it. */
const NEEDS: readonly Row[] = [
  [
    'It runs for minutes or hours. If your script dies, the work is gone.',
    'The run lives on the server. Disconnect, come back, carry on.',
    name('Session'),
  ],
  [
    'The model writes commands at run time, and they run on your machine.',
    'Tools run in an isolated sandbox, not in your process.',
    name('Environment'),
  ],
  [
    'It needs tokens, and code in the loop can read them.',
    'Credentials stay outside the model and the sandbox.',
    name('Vault'),
  ],
  [
    'Later, someone asks what it did.',
    'Every message, tool call and result is recorded, in order.',
    name('Events'),
  ],
];

/** Slide 10: the nine resources as the API has them. */
const ENDPOINTS: readonly Row[] = [
  [name('Agent'), <code>/v1/agents</code>, 'A versioned definition'],
  [name('Environment'), <code>/v1/environments</code>, 'A reusable sandbox template'],
  [name('Session'), <code>/v1/sessions</code>, 'One run of an agent'],
  [name('Events'), <code>{'/v1/sessions/{id}/events'}</code>, 'A session’s log, and its stream'],
  [name('Vault'), <code>/v1/vaults</code>, 'Credentials for MCP servers'],
  [name('File'), <code>/v1/files</code>, 'An asset a session mounts'],
  [name('MemoryStore'), <code>/v1/memory_stores</code>, 'Memory that outlives a session'],
  [name('Skill'), <code>/v1/skills</code>, 'Know-how an agent loads on demand'],
  [name('Trigger'), <code>/v1/triggers</code>, 'Starts sessions on a schedule'],
];

const lab = (n: number, minutes: number, title: string) => (
  <>
    {name(`Lab ${n} · ${minutes} min`)}
    <br />
    {title}
  </>
);

/** Slide 31: the tutorial's own lab table, and what each lab uses from here. */
const LABS: readonly Row[] = [
  [
    lab(0, 3, 'Set up'),
    <>
      Fill in <code>.env</code> from your team card. Check that every service answers.
    </>,
    'Your address and your key.',
  ],
  [lab(1, 5, 'Hello, agent'), 'Create an agent and talk to it.', 'Agent, Environment, Session, Events: the four calls.'],
  [
    lab(2, 8, 'Hello, streaming SQL'),
    'In SQL Workspace, build the view over the login topic, and a table for flags.',
    'The left-hand box. There is no Orca in this one.',
  ],
  [
    lab(3, 9, 'Agent + live context'),
    'Give the agent SQL tools. Add a burst of failed logins, and ask again.',
    'Tools from an MCP server, the allow-list, a Vault.',
  ],
  [
    lab(4, 5, 'Agent acts, human approves'),
    'Let the agent write one row, with your OK.',
    'A permission policy that asks before it acts.',
  ],
];

type Def = {
  readonly id: number;
  readonly section: string;
  readonly notes: readonly string[];
  readonly body: ReactNode;
};

const defs: readonly Def[] = [
  /* ── Open ────────────────────────────────────────────────────────── */

  {
    id: 1,
    section: OPEN,
    notes: [
      'Welcome. This is Orca 101: about forty minutes, and it assumes you have not met Orca before.',
      'What Orca Agent Engine is, the resources and the API it gives you, and how to build an agent on it.',
      'At ten you do all of it by hand in the tutorial. This talk is so that none of it is a surprise.',
    ],
    body: (
      <Frame variant="opener">
        <Lockup />
        <div className="grow" />
        <p className="eyebrow">Data + Agent Hackathon · Data Streaming Summit 2026</p>
        <h1 style={{ marginTop: 16 }}>Orca 101</h1>
        <p className="sub" style={{ marginTop: 18, maxWidth: 760, fontSize: 24, color: 'var(--grey-00)' }}>
          What Orca Agent Engine is, its resources and its API, and how to build an agent on it.
        </p>
        <p className="byline" style={{ marginTop: 26 }}>
          Sijie Guo &nbsp;·&nbsp; Co-founder &amp; CEO, StreamNative
        </p>
        <div className="grow" />
      </Frame>
    ),
  },
  {
    id: 2,
    section: OPEN,
    notes: [
      'One sentence first. Orca Agent Engine is a server that runs AI agents for you.',
      'You describe an agent to it: which model, what instructions, which tools it may use. The engine runs that agent, and you talk to it through an API.',
      'It is open source under Apache 2.0, sponsored by StreamNative, and in Developer Preview, so the API can still change.',
      'You can run it yourself, or use it hosted on StreamNative Cloud. Each team has a hosted one today; its address is on your team card.',
      'To try it on a laptop there is ork local. That is a trial stack: its sandbox is not isolated, so run only code you trust there.',
      'Four parts from here: what it is, its resources and its API, each resource in turn, then how to build an agent.',
    ],
    body: (
      <Frame>
        <Head
          eyebrow="What is Orca Agent Engine?"
          sub="It is a server. You describe an agent to it: which model, what instructions, which tools. It runs that agent, and you talk to it through an API."
        >
          Orca Agent Engine <Hi>runs AI agents for you.</Hi>
        </Head>
        <div className="grow" />
        <div className="cols cols-3">
          <Panel label="Open source" name="Apache-2.0">
            Sponsored by StreamNative. It is in Developer Preview, so the API can still change.
          </Panel>
          <Panel label="Where it runs" name="Yours, or hosted">
            Run it yourself, or use it hosted on StreamNative Cloud. Your team has a hosted one today.{' '}
            <code>ork local</code> starts a trial stack on a laptop.
          </Panel>
          <Panel label="How you use it" name="One API">
            From the <code>ork</code> command line, a Python or TypeScript SDK, or plain HTTP.
          </Panel>
        </div>
        <div className="grow" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span className="cap">The next 40 minutes</span>
          <div className="chips">
            <span className="chip">01 What it is</span>
            <span className="chip">02 Resources and the API</span>
            <span className="chip">03 Resource by resource</span>
            <span className="chip">04 Build an agent</span>
          </div>
        </div>
      </Frame>
    ),
  },
  {
    id: 3,
    section: OPEN,
    notes: [
      'One example runs through the whole talk, and it is the one you build at ten, in five short labs.',
      'A fictional bank streams every login attempt into Kafka. Somewhere in that stream, an attacker is guessing passwords.',
      'Left box: the data platform. Kafka holds the logins. SQL Workspace is the platform’s streaming SQL, and it keeps a running count of failures per account. I will call that the view.',
      'Right box: Orca. You ask who is under attack. The agent queries the view itself, with SQL tools the platform offers, works out which account it is, and flags it, but only after you say yes.',
      'The rest of this talk is the right-hand box. On later slides, a tag in the corner names the lab where you do what the slide shows.',
    ],
    body: (
      <Frame>
        <Head
          eyebrow="The example · you build it at 10:00, in five labs"
          sub="A fictional bank streams every login attempt into Kafka. Somewhere in that stream, an attacker is guessing passwords."
        >
          Spot an attacker in live data. <Hi>Act once a person approves.</Hi>
        </Head>
        <Diagram style={{ marginTop: 8 }}>
          <WorkflowDiagram />
        </Diagram>
        <div className="cols cols-3" style={{ marginTop: 8 }}>
          <div>
            <p className="eyebrow" style={{ fontSize: 12.5 }}>
              01, 02 · The data platform
            </p>
            <p className="sub" style={{ fontSize: 16.5, marginTop: 5 }}>
              Kafka holds the logins. SQL Workspace, the platform&rsquo;s streaming SQL, keeps a live count of
              failures per account: the view.
            </p>
          </div>
          <div>
            <p className="eyebrow" style={{ fontSize: 12.5 }}>
              03 · You ask, the agent reads
            </p>
            <p className="sub" style={{ fontSize: 16.5, marginTop: 5 }}>
              &ldquo;Who is under attack right now?&rdquo; The agent queries the view itself, with SQL tools the
              platform offers.
            </p>
          </div>
          <div>
            <p className="eyebrow" style={{ fontSize: 12.5 }}>
              04 · You approve, it writes
            </p>
            <p className="sub" style={{ fontSize: 16.5, marginTop: 5 }}>
              It flags the account, one row in a table, and only after you say yes. This talk is the right-hand
              box.
            </p>
          </div>
        </div>
      </Frame>
    ),
  },

  /* ── 01 What it is ───────────────────────────────────────────────── */

  {
    id: 4,
    section: WHAT,
    notes: [
      'Start from zero. A language model takes text in and gives text out. On its own it cannot do anything.',
      'Give it tools and it can. A tool is a function the model may ask for: run this SQL query, read this file. But the model only writes the request. A program has to run the tool and hand the result back.',
      'That goes round until the model answers in words. A model working in that loop, with tools, is an agent. The program that drives the loop is a harness.',
      'Claude Agent SDK, Codex and Pi are harnesses. You can run one yourself: a script on a laptop is enough for a demo.',
      'Everything on this slide is grey, because none of it is Orca yet. In these diagrams, lime means Orca runs it.',
    ],
    body: (
      <Frame variant="deep">
        <Head eyebrow="01 · What it is">
          An agent is a model working in a loop, <Hi>with tools.</Hi>
        </Head>
        <div className="split" style={{ marginTop: 14 }}>
          <div className="points">
            <div>
              <h3>The model asks for a tool</h3>
              <p>
                A tool is a function: run a SQL query, read a file, call an API. The model writes the request. It
                cannot run anything itself.
              </p>
            </div>
            <div>
              <h3>A harness runs the loop</h3>
              <p>
                It calls the model, runs the tool the model asked for, and hands the result back, until the model
                answers in words.
              </p>
            </div>
            <div>
              <h3>You can run one yourself</h3>
              <p>Claude Agent SDK, Codex and Pi are harnesses. A script on a laptop is enough for a demo.</p>
            </div>
          </div>
          <Diagram>
            <LoopDiagram />
          </Diagram>
        </div>
      </Frame>
    ),
  },
  {
    id: 5,
    section: WHAT,
    notes: [
      'A script on a laptop is fine for a demo. Here is what breaks when it is real.',
      'The work takes minutes or hours. If your process dies, the conversation dies with it.',
      'The model writes commands at run time. Nobody reviewed them, and they run wherever the loop runs.',
      'It needs credentials, and code running in the loop can read whatever the loop can read.',
      'And later, someone asks what it did.',
      'Software that takes care of these four is an agent runtime, and Orca Agent Engine is one. It does not replace the harness: it runs the harness for you, on a server, and adds these four.',
      'The right-hand column is four of the nine resources you meet in a few minutes.',
    ],
    body: (
      <Frame>
        <Head eyebrow="From a script to a service">
          Running an agent for real <Hi>takes four more things.</Hi>
        </Head>
        <div className="grow" />
        <Table
          cols="1fr 1fr 170px"
          head={['In a script on your laptop', 'On Orca Agent Engine', 'The resource']}
          rows={NEEDS}
        />
        <p className="statement" style={{ fontSize: 27, marginTop: 34 }}>
          An agent runtime runs the harness for you, and adds these four. <Hi>Orca Agent Engine is one.</Hi>
        </p>
        <div className="grow" />
      </Frame>
    ),
  },
  {
    id: 6,
    section: WHAT,
    notes: [
      'So where does Orca sit when a team builds an agent.',
      'Writing the agent, testing it and monitoring it are jobs for your own tooling. That is what "your tools" means on this picture. It does not mean the tools an agent calls.',
      'Orca is the part in the middle. It runs the agent, and it governs it while it runs: which tools and credentials it may use, when it has to ask a person, and what gets recorded.',
      'It is a runtime, not a library for writing agents. The agent logic comes from the harness you pick. Orca keeps the record, and your monitoring reads it.',
    ],
    body: (
      <Frame>
        <Head
          eyebrow="Where it fits"
          sub={
            <>
              Orca runs agents; it is not a library for writing them. &ldquo;Your tools&rdquo; are your own tooling
              for writing, testing and monitoring. Govern means deciding what an agent may touch, and keeping the
              record.
            </>
          }
        >
          Build and test with your tools. <Hi>Run and govern with Orca.</Hi>
        </Head>
        <Figure
          src={lifecycle}
          alt="The agent development lifecycle: build, test and monitor are your tools; run and govern are Orca Agent Engine, the runtime at the centre"
          style={{ marginTop: 12 }}
        />
      </Frame>
    ),
  },
  {
    id: 7,
    section: WHAT,
    notes: [
      'Inside the box. Your code talks to one part, the Registry. It is the API, and it stores everything you declare.',
      'The Harness Server runs the loop from three slides ago.',
      "The Sandbox is an isolated container where the agent's built-in tools run: a shell, files.",
      'The AI Gateway makes the calls that leave. Calls to MCP servers always go through it. MCP, the Model Context Protocol, is the standard way a server offers tools to an agent over the network. The SQL tools in today’s example are an MCP server.',
      "In today's set-up the gateway serves the model as well. On a default install the harness calls the model provider directly.",
      'Every step lands in the Event Store. The gateway is drawn inside the box because calls leave through it, but it is a separate Orca product and it is not open source; the engine is.',
      'In your environment today, all of this is already running.',
    ],
    body: (
      <Frame>
        <Head eyebrow="Inside Orca Agent Engine">
          Declare an agent. <Hi>The engine runs it.</Hi>
        </Head>
        <Figure
          src={architecture}
          alt="Agent Engine architecture: SDK, CLI and UI call the Registry; the Registry works with the Harness Server; the Harness Server uses the Sandbox; calls leave through the AI Gateway to MCP servers and model providers; transcripts and audit logs land in the Event Store"
          style={{ marginTop: 10 }}
        />
        <div className="roles" style={{ marginTop: 10 }}>
          <div>
            <h4>Registry</h4>
            <p>The API you call. It stores what you declare.</p>
          </div>
          <div>
            <h4>Harness Server</h4>
            <p>Runs the agent loop.</p>
          </div>
          <div>
            <h4>Sandbox</h4>
            <p>Runs the built-in tools: shell and files.</p>
          </div>
          <div>
            <h4>AI Gateway</h4>
            <p>Calls MCP servers: tools hosted elsewhere.</p>
          </div>
          <div>
            <h4>Event Store</h4>
            <p>Keeps the log of every session.</p>
          </div>
        </div>
        <p className="cap" style={{ marginTop: 10 }}>
          The AI Gateway is drawn inside because calls leave through it. It is a separate Orca product, and not
          open source.
        </p>
      </Frame>
    ),
  },

  /* ── 02 Resources and the API ────────────────────────────────────── */

  {
    id: 8,
    section: MODEL,
    notes: [
      'Part two. What you work with in Orca is a set of records, which we call resources. These nine are the ones you need today.',
      'Start in the middle. A session is one run of an agent.',
      'It points at an agent, which is the definition, and at an environment, which is where its tools run. The arrow says it pins a version: an agent has versions, and a session keeps the one it started with. Everything it does comes out as events.',
      'A trigger creates sessions for you. And a session can use four more things: a vault for credentials, files, a memory store, and skills.',
      'All nine live in a Workspace. Each team has one today. Do not confuse it with SQL Workspace from the example: this one is Orca’s, the container your key opens.',
      'The labs use five of the nine. We go through all of them in a moment; first, how you call them.',
    ],
    body: (
      <Frame variant="deep">
        <Head
          eyebrow="02 · Resources and the API"
          sub="A resource is a record you create through the API. Read the map from the centre: a session is one run of an agent, in an environment, and everything it does comes out as events."
        >
          Nine resources. <Hi>A session ties them together.</Hi>
        </Head>
        <Figure
          src={resourceModel}
          alt="The resource model: a Session pins a version of an Agent, runs in an Environment and produces Events; a Trigger creates Sessions; a Session mounts or references Vaults, Files, MemoryStores and Skills"
          style={{ marginTop: 10 }}
        />
        <p className="cap" style={{ marginTop: 8 }}>
          All nine live in a Workspace: the container for one team&rsquo;s resources. Your team has one.
        </p>
        <p className="cap" style={{ marginTop: 3 }}>
          The labs use five: Agent, Environment, Session, Events and Vault. The other four are for your project.
        </p>
      </Frame>
    ),
  },
  {
    id: 9,
    section: MODEL,
    notes: [
      'Before the detail, the shape. Four requests take you from nothing to a reply.',
      'Create an environment, and you get an id. Every session needs one, whether or not its agent uses the sandbox.',
      'Create an agent, and you get an id and a version. Create a session from those two ids, and you get a session id.',
      "Then post a message to the session's events, and read its event stream until it goes idle. Idle means it has stopped.",
      'That is Lab 1. Almost everything else in this talk is a variation on these four.',
    ],
    body: (
      <Frame>
        <Head eyebrow="The shape of the API" sub="Each create returns an id. The next call takes it.">
          The API, <Hi>in four calls.</Hi>
        </Head>
        <div className="grow" />
        <div className="calls">
          <Call n={1} name="Environment" endpoint={['POST /v1/environments']} send="A name" get="An environment id">
            The template for a sandbox, where tools will run. Every session needs one.
          </Call>
          <Call
            n={2}
            name="Agent"
            endpoint={['POST /v1/agents']}
            send="A name, a model, a system prompt"
            get="An agent id, at version 1"
          >
            What to run: a model and its instructions.
          </Call>
          <Call
            n={3}
            name="Session"
            endpoint={['POST /v1/sessions']}
            send="The agent id and the environment id"
            get="A session id"
          >
            One run of that agent, in that environment.
          </Call>
          <Call
            n={4}
            name="Events"
            endpoint={['POST /v1/sessions/{id}/events', 'GET  /v1/sessions/{id}/events/stream']}
            send={
              <>
                A <code>user.message</code> event
              </>
            }
            get="The reply, as a stream of events"
          >
            Send a message. Read the stream until the session goes idle: it has stopped.
          </Call>
        </div>
        <div className="grow" />
        <p className="sub" style={{ fontSize: 18 }}>
          In Lab 1 the reply is three events: <code>session.status_running</code>, <code>agent.message</code>,{' '}
          <code>session.status_idle</code>. <code>ork</code> and the SDKs make these same four requests.
        </p>
      </Frame>
    ),
  },
  {
    id: 10,
    section: MODEL,
    notes: [
      'The API model on one slide. It is REST, and each resource is a collection under /v1.',
      'Two kinds of call. You declare eight of the nine: create, get, list, and update or archive where that makes sense.',
      'The ninth is events, and with those you converse: post them to a session, and read its stream.',
      'The CLI follows the same nouns: ork agent, then the resource, then the verb.',
      'Your endpoint and your key resolve to one Workspace, so you never pass a workspace id.',
      "Claude Managed Agents is Anthropic's hosted API for agents. If you have used it: 72 operations are shared with that API, and the differences are documented.",
    ],
    body: (
      <Frame>
        <Head eyebrow="The API model">
          One REST API. <Hi>A collection per resource.</Hi>
        </Head>
        <div className="split wide-left" style={{ marginTop: 16 }}>
          <div className="stack" style={{ justifyContent: 'center' }}>
            <Table dense cols="150px 256px 1fr" head={['Resource', 'Endpoint', 'What it is']} rows={ENDPOINTS} />
          </div>
          <div className="points">
            <div>
              <h3>Declare</h3>
              <p>Create, get and list on eight of them. Update, and archive or delete, where they apply.</p>
            </div>
            <div>
              <h3>Converse</h3>
              <p>The ninth is events. POST them to a session, and GET its stream to follow the reply.</p>
            </div>
            <div>
              <h3>The CLI follows the nouns</h3>
              <p>
                <code>ork agent sessions create</code>, <code>ork agent vaults list</code>. The agent itself is{' '}
                <code>ork agent create</code>.
              </p>
            </div>
          </div>
        </div>
        <p className="cap" style={{ marginTop: 12 }}>
          Your endpoint and key select one Workspace, so no path carries its id. · 72 operations are shared with
          the Claude Managed Agents API.
        </p>
      </Frame>
    ),
  },

  /* ── 03 Resource by resource ─────────────────────────────────────── */

  {
    id: 11,
    section: RESOURCE,
    notes: [
      'Part three: the nine resources, one at a time. The strip along the top shows where we are.',
      'First, the agent. Earlier I called an agent a model working in a loop. In Orca the word names the definition of that: a record, not a process. Nothing is running when you create one. The loop runs in a session, and that is three slides on.',
      'It is a name, a model and a system prompt, which is its instructions. Tools, MCP servers and skills come when it needs them.',
      'Every update makes a new version and leaves the old one as it was.',
      'The harness is a field too, fixed at creation. A different harness means a second agent.',
      "This is the agent you create in Lab 1. The model is served by the hackathon's gateway today, so you bring no model key.",
    ],
    body: (
      <Frame variant="deep">
        <Head eyebrow={<ResourceNav at="Agent" />} lab="Lab 1">
          An agent is a definition, <Hi>not a process.</Hi>
        </Head>
        <div className="split wide-left" style={{ marginTop: 16 }}>
          <div className="stack" style={{ justifyContent: 'center', gap: 10 }}>
            <Code lang="json" size="xl" code={AGENT_JSON} label="What you declare" />
            <p className="cap">Today the model is served by the hackathon&rsquo;s AI gateway. You bring no model key.</p>
          </div>
          <div className="points">
            <div>
              <h3>The word means the definition</h3>
              <p>
                A name, a model and a system prompt: its instructions. Nothing runs when you create one. The loop
                you saw earlier runs in a session.
              </p>
            </div>
            <div>
              <h3>Every update is a new version</h3>
              <p>
                The old one stays as it was. Pass the version you read, and an update based on a stale one is
                rejected.
              </p>
            </div>
            <div>
              <h3>The harness is a field</h3>
              <p>Fixed when the agent is created: Claude Agent SDK by default, Codex SDK or Pi SDK.</p>
            </div>
          </div>
        </div>
        <ApiLine api="/v1/agents" cli="ork agent create · get · list · update · archive · versions" />
      </Frame>
    ),
  },
  {
    id: 12,
    section: RESOURCE,
    notes: [
      'Still the agent: its tools. A tool is declared on the agent, and there are three kinds.',
      'The built-in toolset runs in the sandbox: shell, files, search.',
      'An MCP toolset is the tools of one MCP server. That is how your agent reaches the view in SQL Workspace today.',
      'A custom tool is your own code: the session pauses and hands you the call.',
      'Built-in and MCP tools each carry a permission policy. Built-in tools run without asking by default; MCP tools ask. A custom tool has no policy, because your application is the one running it.',
    ],
    body: (
      <Frame>
        <Head eyebrow={<ResourceNav at="Agent" detail="tools" />} lab="Labs 3 and 4">
          Tools are part of the agent. <Hi>There are three kinds.</Hi>
        </Head>
        <div className="grow" />
        <div className="cols cols-3">
          <Panel label={<code>agent_toolset</code>} name="Built in" fine="DEFAULT POLICY: always_allow">
            Eight tools that run in the session&rsquo;s sandbox: bash, read, write, edit, glob, grep, list and
            delete.
          </Panel>
          <Panel label={<code>mcp_toolset</code>} name="From an MCP server" fine="DEFAULT POLICY: always_ask">
            MCP, the Model Context Protocol, is how a server offers tools over the network. You name the server
            on the agent, and choose which of its tools to allow.
          </Panel>
          <Panel label={<code>custom</code>} name="Your own code" fine="NO POLICY: YOUR APPLICATION RUNS IT">
            Your application executes it. The session pauses, hands you the call, and waits for the result.
          </Panel>
        </div>
        <div className="cols cols-2" style={{ marginTop: 18 }}>
          <p className="sub" style={{ fontSize: 17.5 }}>
            A permission policy sits on each built-in and MCP tool. <code>always_allow</code> runs it.{' '}
            <code>always_ask</code> pauses the session until your application answers.
          </p>
          <p className="sub" style={{ fontSize: 17.5 }}>
            A session has no general web access. Reach the outside through an MCP server.
          </p>
        </div>
        <div className="grow" />
        <ApiLine
          api="tools and mcp_servers, on /v1/agents"
          cli="ork agent update <agent id> --tool-json … --mcp-server …"
        />
      </Frame>
    ),
  },
  {
    id: 13,
    section: RESOURCE,
    notes: [
      "The environment is the template a session's sandbox is built from. A name is all it takes to create one.",
      'A sandbox is an isolated container: the session gets its own filesystem and shell.',
      'Creating a session provisions nothing. The first message is what acquires a sandbox.',
      'Leave it idle and the sandbox is released, sixty seconds by default. The log stays.',
      'The next message gets a fresh sandbox, and the harness resumes from the log.',
    ],
    body: (
      <Frame>
        <Head eyebrow={<ResourceNav at="Environment" />} lab="Lab 1">
          The environment is the template. <Hi>The sandbox is disposable.</Hi>
        </Head>
        <div className="split wide-left" style={{ marginTop: 14 }}>
          <div className="stack">
            <Diagram>
              <RuntimeDiagram on={lit('registry', 'store')} />
            </Diagram>
            <p className="cap">Lit: what remains while a session is idle. Its record, and its log.</p>
          </div>
          <div className="stack" style={{ justifyContent: 'center' }}>
            <Steps
              items={[
                { t: 'Session created', d: 'Nothing is provisioned. It starts idle.' },
                { t: 'First message', d: 'A sandbox is acquired: an isolated container, built from the environment.' },
                { t: 'Idle for 60 seconds, by default', d: 'The sandbox is released. Scratch files go with it.' },
                { t: 'Next message', d: 'A fresh sandbox. The harness resumes from the log.' },
              ]}
            />
          </div>
        </div>
        <ApiLine api="/v1/environments" cli="ork agent environments create · get · list · update · archive · delete" />
      </Frame>
    ),
  },
  {
    id: 14,
    section: RESOURCE,
    notes: [
      'A session is one run of an agent. It pins the agent version it started with.',
      'Edit the agent and you get version 2. Sessions already under way keep version 1.',
      'So you can change an agent while people are using it, and nothing shifts under a conversation.',
      'A turn is one message from you and everything the agent does in answer. A session is running while the agent works, and idle when it stops: either it is done, or it is waiting for you. Those are its only two states.',
      'Lab 1 has you start two sessions and see both on version 1.',
    ],
    body: (
      <Frame>
        <Head eyebrow={<ResourceNav at="Session" />} lab="Lab 1">
          A session is a run. <Hi>It pins the version it started with.</Hi>
        </Head>
        <div className="split" style={{ marginTop: 14 }}>
          <div className="points">
            <div>
              <h3>One definition, many runs</h3>
              <p>Each session has its own log and, once it has work, its own sandbox.</p>
            </div>
            <div>
              <h3>An edit never reaches a running session</h3>
              <p>Updating the agent makes version 2. Sessions 1 and 2 keep version 1 for as long as they live.</p>
            </div>
            <div>
              <h3>It is idle, or it is running</h3>
              <p>
                A turn is one message and everything the agent does in answer. A session is <code>running</code>{' '}
                while it works, and <code>idle</code> when it stops: done, or waiting for you.
              </p>
            </div>
          </div>
          <Diagram>
            <VersionsDiagram name="hello-agent" />
          </Diagram>
        </div>
        <ApiLine api="/v1/sessions" cli="ork agent sessions create · get · list · update · archive · delete" />
      </Frame>
    ),
  },
  {
    id: 15,
    section: RESOURCE,
    notes: [
      'Everything that happens in a session is an event in its log, in order. If you know Kafka: it is an append-only log, one per session.',
      'You append to it, the agent appends to it, and you read it back as a stream.',
      "Four families. Yours start with user. The agent's start with agent. The session reports its own status. Spans carry timing and token counts.",
      'The one to remember is session.status_idle: the session has stopped, and its stop reason says why.',
      'end_turn means the turn is done. requires_action means it is waiting for you, and the turn carries on once you answer. You meet that one in Lab 4.',
    ],
    body: (
      <Frame>
        <Head
          eyebrow={<ResourceNav at="Events" />}
          lab="Lab 1"
          sub="A session keeps an append-only log. You append to it, the agent appends to it, and you read it back as a stream."
        >
          Everything that happens <Hi>is an event in the log.</Hi>
        </Head>
        <div className="split even" style={{ marginTop: 12 }}>
          <Diagram>
            <TimelineDiagram />
          </Diagram>
          <div className="stack" style={{ justifyContent: 'center', gap: 12 }}>
            <div className="fam">
              <div className="r">
                <div className="k">From your application</div>
                <div className="v">
                  <span>user.message</span>
                  <span>user.tool_confirmation</span>
                  <span>user.custom_tool_result</span>
                </div>
              </div>
              <div className="r">
                <div className="k">From the agent</div>
                <div className="v">
                  <span>agent.message</span>
                  <span>agent.tool_use</span>
                  <span>agent.tool_result</span>
                  <span>agent.mcp_tool_use</span>
                  <span>agent.mcp_tool_result</span>
                  <span>agent.custom_tool_use</span>
                </div>
              </div>
              <div className="r">
                <div className="k">From the session itself</div>
                <div className="v">
                  <span>session.status_running</span>
                  <span>session.status_idle</span>
                  <span>session.error</span>
                </div>
              </div>
              <div className="r">
                <div className="k">Timing and usage</div>
                <div className="v">
                  <span>span.model_request_start</span>
                  <span>span.model_request_end</span>
                </div>
              </div>
            </div>
            <p className="sub" style={{ fontSize: 16.5 }}>
              The session stops at <code>session.status_idle</code>. Its <code>stop_reason</code> says why:{' '}
              <code>end_turn</code>, <code>requires_action</code> or <code>retries_exhausted</code>.
            </p>
          </div>
        </div>
        <ApiLine
          api="/v1/sessions/{id}/events · /v1/sessions/{id}/events/stream"
          cli="ork agent sessions events send · list · stream"
        />
      </Frame>
    ),
  },
  {
    id: 16,
    section: RESOURCE,
    notes: [
      'Where do credentials live. Not in the prompt, and not in the sandbox.',
      'A vault holds credentials, each bound to one MCP server URL. An agent never names a vault. The session does, by id, when it is created.',
      "When a tool call goes out to that MCP server, the gateway finds the matching credential in the session's vaults and attaches it on the way out. On the picture, upstream is the MCP server being called.",
      'This covers the credentials the gateway manages. Lab 3 walks you through creating one with a browser login.',
    ],
    body: (
      <Frame>
        <Head eyebrow={<ResourceNav at="Vault" />} lab="Lab 3">
          The session names a vault. <Hi>The gateway attaches the key.</Hi>
        </Head>
        <Figure
          src={custody}
          alt="Where the secret lives: the session names a vault by id; the AI Gateway resolves it against the vault and injects the real credential into the call to the MCP server"
          style={{ marginTop: 12 }}
        />
        <div className="cols cols-3" style={{ marginTop: 12 }}>
          <p className="sub" style={{ fontSize: 16.5 }}>
            A vault holds credentials. Each is bound to one MCP server URL: a bearer token or an OAuth grant.
          </p>
          <p className="sub" style={{ fontSize: 16.5 }}>
            A session names its vaults in <code>vault_ids</code>, set when the session is created.
          </p>
          <p className="sub" style={{ fontSize: 16.5 }}>
            Tokens never enter the prompt or the sandbox, and the server can refresh them.
          </p>
        </div>
        <ApiLine
          api="/v1/vaults · /v1/vaults/{id}/credentials"
          cli="ork agent vaults create · ork agent vaults credentials create"
        />
      </Frame>
    ),
  },
  {
    id: 17,
    section: RESOURCE,
    notes: [
      'The tutorial stops at the first five resources. These four are for the project you build after it.',
      'A file is something for a session to read: a PDF, a dataset. You upload it once and reference it by id from any number of sessions.',
      'It shows up as a path in the sandbox, and the agent cannot change it. The agent reads it with its built-in file tools, which the agent in the tutorial does not have yet.',
      "Outputs go the other way: what the agent writes under the outputs directory becomes the session's outputs.",
    ],
    body: (
      <Frame>
        <Head eyebrow={<ResourceNav at="File" />} lab={AFTER_LABS}>
          A file is uploaded once <Hi>and mounted read-only.</Hi>
        </Head>
        <div className="split" style={{ marginTop: 14 }}>
          <div className="points">
            <div>
              <h3>Something for a session to read</h3>
              <p>A PDF to summarise, a dataset to analyse. Upload it once; any number of sessions can use it.</p>
            </div>
            <div>
              <h3>It shows up as a path</h3>
              <p>
                Mounted in the sandbox under <code>/mnt/session/uploads/&lt;file_id&gt;/</code>. The agent can read
                it and cannot change it.
              </p>
            </div>
            <div>
              <h3>Outputs go the other way</h3>
              <p>
                Files the agent writes under <code>/mnt/session/outputs/</code> become the session&rsquo;s outputs.
              </p>
            </div>
          </div>
          <div className="stack" style={{ justifyContent: 'center', gap: 12 }}>
            <Code lang="bash" size="md" label="Upload a file, then attach it" code={FILE_CLI} />
            <p className="sub" style={{ fontSize: 16.5 }}>
              An upload can be up to 500 MB.
            </p>
          </div>
        </div>
        <ApiLine api="/v1/files · /v1/sessions/{id}/resources" cli="ork agent files create · get · list · delete" />
      </Frame>
    ),
  },
  {
    id: 18,
    section: RESOURCE,
    notes: [
      'A sandbox is thrown away once its session has sat idle. A memory store is how something survives.',
      'To the agent it is a folder. It reads and writes notes there like any directory.',
      'You choose read-only or read-write when you attach it. What one session writes, every session that mounts the store can read, so do not mix things that should stay apart.',
      'Every write is kept as a version, which you can audit and redact.',
    ],
    body: (
      <Frame>
        <Head eyebrow={<ResourceNav at="MemoryStore" />} lab={AFTER_LABS}>
          A memory store is a directory <Hi>that outlives the session.</Hi>
        </Head>
        <div className="split" style={{ marginTop: 14 }}>
          <div className="points">
            <div>
              <h3>Notes that carry over</h3>
              <p>A sandbox is thrown away. What the agent writes to a memory store is there for the next session.</p>
            </div>
            <div>
              <h3>To the agent, a folder</h3>
              <p>
                Mounted at <code>/mnt/memory/&lt;store-name&gt;/</code>. The agent reads and writes it like any
                directory.
              </p>
            </div>
            <div>
              <h3>You choose the access</h3>
              <p>
                <code>read_only</code> or <code>read_write</code>, per session. What one session writes, every
                session that mounts the store can read.
              </p>
            </div>
          </div>
          <div className="stack" style={{ justifyContent: 'center', gap: 12 }}>
            <Code lang="bash" size="md" label="Create a store, then attach it" code={MEMORY_CLI} />
            <p className="sub" style={{ fontSize: 16.5 }}>
              A session mounts up to 8 stores. Every write is kept as a version you can audit and redact.
            </p>
          </div>
        </div>
        <ApiLine
          api="/v1/memory_stores"
          cli="ork agent memory-stores create · get · list · update · archive · delete"
        />
      </Frame>
    ),
  },
  {
    id: 19,
    section: RESOURCE,
    notes: [
      'A skill is packaged know-how: a folder with a SKILL.md in it, plus whatever reference files and scripts go with it.',
      'You upload it once and list it on the agent. The agent opens it when the task calls for it, not on every turn.',
      'The map earlier draws the skill on the session, because that is where it is mounted. You declare it on the agent.',
      "One thing to know before you try it this afternoon: a skill is read-only files, so the agent needs the built-in read tool. The tutorial's agent has no built-in tools. Send agent_toolset together with your MCP toolset: an update replaces the whole tools list.",
    ],
    body: (
      <Frame>
        <Head eyebrow={<ResourceNav at="Skill" />} lab={AFTER_LABS}>
          A skill is know-how <Hi>the agent opens when it needs it.</Hi>
        </Head>
        <div className="split" style={{ marginTop: 14 }}>
          <div className="points">
            <div>
              <h3>A folder with a SKILL.md</h3>
              <p>Instructions, reference files, helper scripts. You upload it as a bundle; each upload is a version.</p>
            </div>
            <div>
              <h3>Listed on the agent</h3>
              <p>
                In its <code>skills</code>, optionally pinned to a version. The agent opens the skill when the task
                calls for it.
              </p>
            </div>
            <div>
              <h3>It needs the read tool</h3>
              <p>
                A skill is mounted as read-only files. Without the built-in <code>read</code> tool enabled, the
                session does not start.
              </p>
            </div>
          </div>
          <div className="stack" style={{ justifyContent: 'center', gap: 12 }}>
            <Code lang="bash" size="md" label="Upload a skill, then list it on the agent" code={SKILL_CLI} />
            <p className="sub" style={{ fontSize: 16.5 }}>
              A bundle holds up to 128 files, 30 MB in total.
            </p>
          </div>
        </div>
        <ApiLine api="/v1/skills" cli="ork agent skills create · get · list · delete · versions" />
      </Frame>
    ),
  },
  {
    id: 20,
    section: RESOURCE,
    notes: [
      'Last of the nine. A trigger starts sessions for you, so nobody has to type.',
      'On a cron schedule it creates a session and sends its payload as the first message.',
      'Each firing is an ordinary session: the same log, the same events, the same tools. It gets the environment and the vaults the trigger names, and nothing else, so name the vault if the agent calls an MCP server. The trigger pins the agent version it was created with.',
      'On StreamNative Cloud a Kafka or Pulsar topic can be the source, so a message on a topic starts a session. That depends on your environment, so ask one of us before you build on it today.',
    ],
    body: (
      <Frame>
        <Head eyebrow={<ResourceNav at="Trigger" />} lab={AFTER_LABS}>
          A trigger <Hi>starts sessions for you.</Hi>
        </Head>
        <div className="split" style={{ marginTop: 14 }}>
          <div className="points">
            <div>
              <h3>A schedule in place of a person</h3>
              <p>On a cron schedule it creates a session, and sends its payload as the first message.</p>
            </div>
            <div>
              <h3>Each firing is an ordinary session</h3>
              <p>
                With the environment and vaults the trigger names. It pins the agent version it was created with.
              </p>
            </div>
            <div>
              <h3>Or a topic in place of a schedule</h3>
              <p>
                On StreamNative Cloud a Kafka or Pulsar topic can be the source: a message starts a session. Ask
                one of us before you build on it today.
              </p>
            </div>
          </div>
          <div className="stack" style={{ justifyContent: 'center', gap: 12 }}>
            <Code lang="bash" size="md" label="A nightly review" code={TRIGGER_CLI} />
            <p className="sub" style={{ fontSize: 16.5 }}>
              Stop and restart one with <code>ork agent triggers pause</code> and <code>unpause</code>.
            </p>
          </div>
        </div>
        <ApiLine api="/v1/triggers" cli="ork agent triggers create · get · list · update · pause · unpause · delete" />
      </Frame>
    ),
  },

  /* ── 04 Build an agent ───────────────────────────────────────────── */

  {
    id: 21,
    section: BUILD,
    notes: [
      'Part four. Before any command, follow one message through everything we just named.',
      'One: your app posts a message. It is an event like any other.',
      "Two: the Registry appends it to the session's log, and the call returns.",
      'Three: the Harness Server reads it and loads the agent version this session pinned.',
      'Four: only now is a sandbox acquired. Creating the session provisioned nothing.',
      'Hops one to five and eight are what happens in Lab 1. The query on the next slide is Lab 3.',
    ],
    body: (
      <Frame variant="deep">
        <Head eyebrow="04 · Build an agent · One message, hop by hop · 1 of 2">
          The message lands in the log. <Hi>The harness picks it up.</Hi>
        </Head>
        <div className="split wide-left" style={{ marginTop: 14 }}>
          <div className="stack">
            <Diagram>
              <RuntimeDiagram
                on={lit('app', 'send', 'registry', 'append', 'store', 'consume', 'harness', 'prepare', 'run', 'sandbox')}
                hops={[1, 2, 3, 4]}
              />
            </Diagram>
            <p className="cap">{ILLUSTRATION}</p>
          </div>
          <div className="stack" style={{ justifyContent: 'center', gap: 18 }}>
            <Steps
              items={[
                {
                  t: 'Your app sends a message',
                  d: (
                    <>
                      A <code>user.message</code>, posted to the session&rsquo;s events.
                    </>
                  ),
                },
                { t: 'The Registry appends it to the log', d: 'The call returns once it is stored.' },
                { t: 'The Harness Server picks it up', d: 'It loads the agent version the session pinned.' },
                { t: 'A sandbox is acquired', d: 'Built from the session’s environment, on the first message.' },
              ]}
            />
            <EventLog lines={[{ type: 'user.message', text: 'Who is under attack right now?' }]} />
          </div>
        </div>
      </Frame>
    ),
  },
  {
    id: 22,
    section: BUILD,
    notes: [
      "Five: the model is called. In today's set-up that goes through the gateway, which holds the provider key.",
      "Six: the model asks for an MCP tool, here the SQL query of Lab 3. The MCP server is the data platform's: this is the arrow between the two boxes of the example. Its policy allows it, so it runs without asking, and the gateway attaches the credential from the session's vault.",
      "Seven: a built-in tool, a file write, which runs in the sandbox. The tutorial's agent has no built-in tools; this hop is here to show the other path.",
      'Eight: the reply is appended to the log like everything before it, your stream delivers it, and the session goes idle.',
      'Read the log top to bottom: that is the whole turn, and you can read it back later. The timing events are left out here.',
    ],
    body: (
      <Frame>
        <Head eyebrow="One message, hop by hop · 2 of 2">
          The turn runs. <Hi>Every step is written to the log.</Hi>
        </Head>
        <div className="split wide-left" style={{ marginTop: 14 }}>
          <div className="stack">
            <Diagram>
              <RuntimeDiagram
                on={lit(
                  'harness',
                  'egress',
                  'gateway',
                  'to-model',
                  'model',
                  'to-mcp',
                  'mcp',
                  'run',
                  'sandbox',
                  'record',
                  'store',
                  'stream',
                  'registry',
                  'app',
                )}
                hops={[5, 6, 7, 8]}
              />
            </Diagram>
            <p className="cap">{ILLUSTRATION}</p>
          </div>
          <div className="stack" style={{ justifyContent: 'center', gap: 14 }}>
            <Steps
              start={5}
              items={[
                { t: 'The model is called', d: 'Through the AI Gateway today. It holds the provider key.' },
                {
                  t: 'The model asks to query the view',
                  d: 'An MCP tool on the data platform, allowed by its policy. The gateway attaches the vault’s credential.',
                },
                { t: 'A file is written in the sandbox', d: 'A built-in tool. Its result is one more event.' },
                { t: 'The reply reaches your app', d: 'Appended to the log, delivered on your stream.' },
              ]}
            />
            <EventLog
              lines={[
                { type: 'session.status_running', text: 'the turn starts' },
                { type: 'agent.mcp_tool_use', text: 'sql_workspace_query' },
                { type: 'agent.mcp_tool_result', text: '1 row: acct_0042' },
                { type: 'agent.tool_use', text: 'write · outputs/report.md' },
                { type: 'agent.tool_result', text: 'written' },
                { type: 'agent.message', text: 'acct_0042 looks taken over.' },
                { type: 'session.status_idle', text: 'stop_reason: end_turn' },
              ]}
            />
          </div>
        </div>
      </Frame>
    ),
  },
  {
    id: 23,
    section: BUILD,
    notes: [
      'Now the tutorial itself, so the next slides make sense.',
      'You clone one repository and put your team card in a file called .env. That is your Agent Engine address and key, plus your Kafka and MCP addresses.',
      'Then you pick one path: the CLI, Python or TypeScript. Each lab is one script in that folder. The script makes the API calls and prints what happened.',
      'So you will not type the commands on the next slides. They are what the scripts send, shown so you know what just happened.',
      'After every step there is a check, and you run it with lab-ork: that is ork with your address and key filled in.',
    ],
    body: (
      <Frame>
        <Head
          eyebrow="How the tutorial works"
          sub="A script makes the API calls and prints what happened. The slides after this one show the calls each script makes."
        >
          In the tutorial, <Hi>each lab is one script.</Hi>
        </Head>
        <div className="split even" style={{ marginTop: 14 }}>
          <div className="stack" style={{ justifyContent: 'center' }}>
            <Steps
              items={[
                {
                  t: 'Clone the tutorial',
                  d: <code>github.com/streamnative/dataplusagent-hackathon-tutorial</code>,
                },
                {
                  t: (
                    <>
                      Put your team card in <code>.env</code>
                    </>
                  ),
                  d: 'Your Agent Engine address and key, and your Kafka and MCP addresses.',
                },
                {
                  t: 'Pick one path, and stay on it',
                  d: 'CLI, Python or TypeScript. A teammate can pick another.',
                },
                {
                  t: (
                    <>
                      Check each step with <code>./lab-ork</code>
                    </>
                  ),
                  d: (
                    <>
                      It is <code>ork</code> with your address and key filled in.
                    </>
                  ),
                },
              ]}
            />
          </div>
          <div className="stack" style={{ justifyContent: 'center', gap: 12 }}>
            <Code lang="bash" size="md" label="CLI · from cli/" code={RUN_CLI} />
            <Code lang="bash" size="md" label="Python · from python/" code={RUN_PYTHON} />
            <Code lang="bash" size="md" label="TypeScript · from typescript/" code={RUN_TS} />
          </div>
        </div>
      </Frame>
    ),
  },
  {
    id: 24,
    section: BUILD,
    notes: [
      'The first thing every script does is connect. One API, and the tutorial has a path for three clients.',
      'The ork CLI, the Python SDK, the TypeScript SDK. There is a Go SDK as well.',
      'All three need the same two values, and both are on your team card. The scripts read them from .env; this is what you write when the code is your own.',
      'No team card? The tutorial has a Local course that runs the same labs on your laptop, and its pages cover the key it uses.',
    ],
    body: (
      <Frame>
        <Head eyebrow="What the scripts do first" lab="Lab 0">
          One API. <Hi>Three ways in.</Hi>
        </Head>
        <div className="grow" />
        <div className="cols cols-3">
          <Code lang="bash" size="sm" label="CLI · ork" code={CONNECT_CLI} />
          <Code lang="python" size="sm" label="Python · runorca" code={CONNECT_PYTHON} />
          <Code lang="ts" size="sm" label="TypeScript · @runorca/orca-sdk" code={CONNECT_TS} />
        </div>
        <div className="cols cols-2" style={{ marginTop: 20 }}>
          <p className="sub" style={{ fontSize: 17.5 }}>
            <code>ORCA_BASE_URL</code> and <code>SN_API_KEY</code> come from your team card. The lab scripts read
            them from <code>.env</code>; this is what you write in your own code.
          </p>
          <p className="sub" style={{ fontSize: 17.5 }}>
            On StreamNative Cloud, the Console is a UI over the same resources.
          </p>
        </div>
        <div className="grow" />
      </Frame>
    ),
  },
  {
    id: 25,
    section: BUILD,
    notes: [
      'These are the four calls from earlier, as the Lab 1 script sends them.',
      'An environment, an agent, a session, and then events: send a message, stream the reply.',
      'The agent is defined by a JSON file in the tutorial repository. All three paths read the same file.',
      'The session names the agent and its version. That is the pinning from the session slide.',
      'The stream starts at cursor zero, the start of the log, so it cannot miss a reply that came back fast.',
      "The comments are the lab's own words. It calls a session a conversation; that is the same thing as a run.",
    ],
    body: (
      <Frame>
        <Head eyebrow="Hello, agent" lab="Lab 1">
          Four calls <Hi>to a first reply.</Hi>
        </Head>
        <div className="grow" />
        <Code lang="bash" label="The calls behind ./l1_hello.sh" code={FOUR_CALLS_CLI} />
        <p className="sub" style={{ fontSize: 16.5, marginTop: 12 }}>
          <code>$ORCA_MODEL</code> comes from <code>.env</code>. <code>ana</code> stands for your name: the scripts
          add it, so teammates in one Workspace do not collide.
        </p>
        <div className="grow" />
      </Frame>
    ),
  },
  {
    id: 26,
    section: BUILD,
    notes: [
      'One turn of a new session, in both SDKs.',
      'Send an event, then read the stream until the session goes idle.',
      'A cursor is a position in the log, like an offset. Start the stream at zero. Opened with no cursor it starts from now, and a short turn can end before it attaches.',
      "This is right for a session's first turn. From cursor zero a later turn replays the earlier ones first, so skip events until the id of the message you just sent; send returns that id. The tutorial's run_turn handles later turns its own way: on the Cloud course it opens the stream first, then sends.",
    ],
    body: (
      <Frame>
        <Head eyebrow={'The turn loop · a session’s first turn'}>
          Send an event. <Hi>Read the stream until it goes idle.</Hi>
        </Head>
        <div className="grow" />
        <div className="cols cols-2">
          <Code lang="python" size="md" label="Python" code={TURN_PYTHON} hl={[14, 15]} />
          <Code lang="ts" size="md" label="TypeScript" code={TURN_TS} hl={[13]} />
        </div>
        <div className="cols cols-3" style={{ marginTop: 14 }}>
          <p className="sub" style={{ fontSize: 16 }}>
            A cursor is a position in the log, like an offset. Start at <code>0</code>: with no cursor a stream
            starts from now, and can miss a short turn.
          </p>
          <p className="sub" style={{ fontSize: 16 }}>
            On a later turn, <code>0</code> replays the earlier turns first. Skip events until the id of your own
            message: <code>send</code> returns it.
          </p>
          <p className="sub" style={{ fontSize: 16 }}>
            The loop stops at <code>session.status_idle</code>. <code>end_turn</code> means done;{' '}
            <code>requires_action</code> means it is waiting for you.
          </p>
        </div>
        <div className="grow" />
      </Frame>
    ),
  },
  {
    id: 27,
    section: BUILD,
    notes: [
      'Lab 3: give the agent live context. Context is the data an agent has in front of it when it answers.',
      "Three things change on the agent. The system prompt becomes the security analyst's, and two fields are new.",
      'mcp_servers names the StreamNative MCP server for your SQL Workspace. Its address is on your team card.',
      'tools turns on exactly two of its tools. Everything else on that server is off, and a tool that is off is never offered to the model.',
      'The Lab 3 script sends this as an update, and that makes version 2.',
    ],
    body: (
      <Frame>
        <Head eyebrow="Live context over MCP" lab="Lab 3">
          Give it live context: <Hi>an MCP server and an allow-list.</Hi>
        </Head>
        <div className="split wide-left" style={{ marginTop: 16 }}>
          <div className="stack" style={{ justifyContent: 'center' }}>
            <Code
              lang="json"
              size="md"
              label="Two new fields · agent/cloud/l3-live-context.json"
              code={MCP_JSON}
              hl={[8]}
            />
          </div>
          <div className="points">
            <div>
              <h3>Name the server, then its tools</h3>
              <p>
                <code>mcp_servers</code> says where it is: <code>SN_MCP_URL</code>, from your team card. An{' '}
                <code>mcp_toolset</code> says which of its tools the agent may call.
              </p>
            </div>
            <div>
              <h3>Off unless you turn it on</h3>
              <p>
                <code>default_config.enabled: false</code> switches every other tool off. A tool that is off is
                never offered to the model.
              </p>
            </div>
            <div>
              <h3>The update is version 2</h3>
              <p>It changes the system prompt too, to the analyst&rsquo;s. Sessions from Lab 1 stay on version 1.</p>
            </div>
          </div>
        </div>
      </Frame>
    ),
  },
  {
    id: 28,
    section: BUILD,
    notes: [
      'That MCP server wants an OAuth login, and the credential goes in a vault.',
      'The first run of the Lab 3 script opens your browser. Approve the login, and ork sends the tokens straight to the vault.',
      "The session carries the vault's id and nothing else.",
      'All three paths use ork for this one step, so Python and TypeScript people need ork installed too.',
    ],
    body: (
      <Frame>
        <Head eyebrow="Credentials" lab="Lab 3">
          The MCP credential <Hi>goes in a vault.</Hi>
        </Head>
        <div className="grow" />
        <Code lang="bash" label="The calls behind the first run of Lab 3" code={VAULT_CLI} hl={[5, 9]} />
        <div className="cols cols-3" style={{ marginTop: 18 }}>
          <p className="sub" style={{ fontSize: 17 }}>
            The first run opens your browser: the MCP server wants an OAuth login. All three paths use{' '}
            <code>ork</code> for this step.
          </p>
          <p className="sub" style={{ fontSize: 17 }}>
            <code>ork</code> sends the tokens straight to the vault. They never enter the prompt or your{' '}
            <code>.env</code>.
          </p>
          <p className="sub" style={{ fontSize: 17 }}>
            The session carries the vault&rsquo;s id. The gateway does the rest.
          </p>
        </div>
        <div className="grow" />
      </Frame>
    ),
  },
  {
    id: 29,
    section: BUILD,
    notes: [
      'Lab 4: one tool that writes, and a policy that makes the agent wait.',
      'Adding the tool is one more agent version, and the script opens a new session on it.',
      'The agent calls the tool. The session goes idle with requires_action, before anything is written.',
      'In the lab the script shows you what the agent wants to write and asks yes or no. It sends your answer as a tool confirmation, naming the tool use event it answers. A denial goes back to the agent with your reason.',
      'The pause is in the engine, not in the prompt. A prompt is a request the model can be talked out of.',
    ],
    body: (
      <Frame>
        <Head eyebrow="Governed actions" lab="Lab 4">
          Ask <Hi>before it acts.</Hi>
        </Head>
        <div className="split even" style={{ marginTop: 16 }}>
          <div className="stack" style={{ justifyContent: 'center', gap: 16 }}>
            <Code lang="json" size="md" label="On the agent: one tool, one policy" code={ASK_JSON} hl={[2]} />
            <Code lang="bash" size="md" label="From your application: the answer" code={CONFIRM_CLI} hl={[4]} />
            <p className="sub" style={{ fontSize: 16 }}>
              In the lab the script asks you y or n, and sends this for you. The id is the{' '}
              <code>agent.mcp_tool_use</code> event&rsquo;s. Adding the tool is one more agent version, on a new
              session.
            </p>
          </div>
          <div className="stack" style={{ justifyContent: 'center', gap: 20 }}>
            <EventLog
              large
              title="What the session does"
              lines={[
                { type: 'agent.mcp_tool_use', text: 'sql_workspace_insert_rows' },
                { type: 'session.status_idle', text: 'stop_reason: requires_action' },
                { type: 'user.tool_confirmation', text: 'allow, or deny with a reason' },
                { type: 'session.status_running', text: 'the turn resumes' },
                { type: 'agent.mcp_tool_result', text: 'the row, or your denial' },
              ]}
            />
            <p className="statement" style={{ fontSize: 27, marginTop: 0 }}>
              The pause is in the engine, <Hi>not in the prompt.</Hi>
            </p>
          </div>
        </div>
      </Frame>
    ),
  },

  /* ── Into the tutorial ───────────────────────────────────────────── */

  {
    id: 30,
    section: NEXT,
    notes: [
      'Back to the first picture, now with names you know.',
      "A stream holds the facts. A materialized view keeps the agent's context fresh. The agent reads it through an allow-list of MCP tools. A person approves the one write, which here is a row in a table.",
      'That is the shape of most data plus agent applications.',
      'Swap the topic, the view and the action, and you have your project.',
    ],
    body: (
      <Frame>
        <Head eyebrow="The shape of a data + agent application">
          Stream, view, agent, <Hi>approval.</Hi>
        </Head>
        <Diagram style={{ marginTop: 8 }}>
          <WorkflowDiagram />
        </Diagram>
        <div className="cols cols-4" style={{ marginTop: 8 }}>
          <div>
            <p className="eyebrow" style={{ fontSize: 12.5 }}>
              01 · A stream
            </p>
            <p className="sub" style={{ fontSize: 16.5, marginTop: 5 }}>
              Kafka holds the facts as they happen.
            </p>
          </div>
          <div>
            <p className="eyebrow" style={{ fontSize: 12.5 }}>
              02 · A materialized view
            </p>
            <p className="sub" style={{ fontSize: 16.5, marginTop: 5 }}>
              The agent&rsquo;s always-fresh context.
            </p>
          </div>
          <div>
            <p className="eyebrow" style={{ fontSize: 12.5 }}>
              03 · An agent
            </p>
            <p className="sub" style={{ fontSize: 16.5, marginTop: 5 }}>
              Reads that context itself, through an allow-list of MCP tools.
            </p>
          </div>
          <div>
            <p className="eyebrow" style={{ fontSize: 12.5 }}>
              04 · An approval gate
            </p>
            <p className="sub" style={{ fontSize: 16.5, marginTop: 5 }}>
              On the one action that changes something: here, a row in a table.
            </p>
          </div>
        </div>
        <p className="sub" style={{ marginTop: 12, color: 'var(--grey-00)' }}>
          Swap the topic, the view and the action, and you have your project.
        </p>
      </Frame>
    ),
  },
  {
    id: 31,
    section: NEXT,
    notes: [
      'Here is what happens at ten. Five labs, about thirty minutes, and you have seen every piece.',
      'Lab 0 connects you. Lab 1 is the four calls.',
      'Lab 2 is the data half. You write SQL in SQL Workspace, in the StreamNative Cloud console: the view the agent will read, and the table it will write to. There is no Orca in it.',
      'Lab 3 gives the agent that view through MCP, with the credential in a vault. Then you add a burst of failed logins to the topic, ask again, and the answer changes. Lab 4 lets it write, once you approve.',
      'Each lab ends with a check, a short quiz and a task to try on your own.',
    ],
    body: (
      <Frame>
        <Head eyebrow="The tutorial · 10:00">
          Next: five labs, <Hi>about 30 minutes.</Hi>
        </Head>
        <div className="grow" />
        <Table cols="330px 1fr 1fr" head={['Lab', 'You', 'What it uses from this talk']} rows={LABS} />
        <div className="grow" />
        <p className="sub" style={{ fontSize: 18 }}>
          Each lab ends with a check, a short quiz and a task to try on your own.
        </p>
      </Frame>
    ),
  },
  {
    id: 32,
    section: NEXT,
    notes: [
      'When the tutorial is done you build your own project. Here is where to take it.',
      'On the data side: join the other preloaded topics into one view that scores each account, or bring your own stream.',
      'On the agent side: your own code as a tool, and the four resources the labs skipped. Hand the agent a runbook as a file, let it keep notes in a memory store, package a playbook as a skill.',
      'The last two depend on what is switched on in your environment, so ask one of us first. The same goes for guardrails, which cap tool calls or cost per session.',
    ],
    body: (
      <Frame>
        <Head eyebrow="Go further">
          When the hello world <Hi>is done.</Hi>
        </Head>
        <div className="grow" />
        <div className="rows">
          <div className="r">
            <div className="k">Richer context</div>
            <div className="v">
              Join the other preloaded topics into one view that scores each account: <code>identity_changes</code>
              , <code>threat_intel</code>, <code>device_signals</code>, <code>payment_events</code>.
            </div>
          </div>
          <div className="r">
            <div className="k">Your own data</div>
            <div className="v">
              Generate a live stream for your idea with ShadowTraffic, covered after the tutorial, and point the
              same pattern at it.
            </div>
          </div>
          <div className="r">
            <div className="k">Your code as a tool</div>
            <div className="v">A custom tool: post to Slack, open a ticket, call your service.</div>
          </div>
          <div className="r">
            <div className="k">Files, memory, skills</div>
            <div className="v">Hand the agent a runbook, let it keep notes, package a playbook.</div>
          </div>
          <div className="r">
            <div className="k">Event-driven agents</div>
            <div className="v">
              A trigger can open a session for each Kafka message, instead of when you type.
              <span className="ask">Ask onsite</span>
            </div>
          </div>
          <div className="r">
            <div className="k">Decisions as events</div>
            <div className="v">
              Let the agent write its decisions to a Kafka topic with <code>kafka_client_produce</code>.
              <span className="ask">Ask onsite</span>
            </div>
          </div>
        </div>
        <div className="grow" />
      </Frame>
    ),
  },
  {
    id: 33,
    section: NEXT,
    notes: [
      'That is Orca 101.',
      'The tutorial is the QR on the left. Orca 101 on the web is on the right: the same material, at your own pace.',
      'The Orca skills teach your coding agent the CLI and the TypeScript SDK.',
      'Find any of us in the room. Now build it.',
    ],
    body: (
      <Frame variant="opener">
        <Lockup />
        <div className="grow" />
        <div className="closer">
          <div>
            <h2>Now build it.</h2>
            <p className="sub" style={{ marginTop: 14, color: 'var(--grey-00)' }}>
              The guided tutorial starts at 10:00.
              <br />
              Five labs, about 30 minutes.
            </p>
            <div className="links scrim" style={{ marginTop: 26 }}>
              <span className="k">DOCS</span>
              <span>docs.runorca.ai</span>
              <span className="k">SKILLS</span>
              <span>github.com/orca-ae/orca-skills</span>
              <span className="k">CLI</span>
              <span>brew install orca-ae/tap/ork</span>
            </div>
          </div>
          <div className="qrs">
            <Qr
              url="https://github.com/streamnative/dataplusagent-hackathon-tutorial"
              title="The tutorial"
              lines={['github.com/streamnative/', 'dataplusagent-hackathon-tutorial']}
            />
            <Qr
              url="https://www.runorca.ai/learn/orca-101"
              title="Orca 101"
              lines={['runorca.ai/learn/orca-101', 'four guides, about 25 minutes']}
            />
          </div>
        </div>
        <div className="grow" />
      </Frame>
    ),
  },
];

export const slides: readonly Slide[] = defs.map(def => ({
  id: def.id,
  section: def.section,
  theme: 'dark',
  notes: def.notes,
  render: () => (
    <PageContext.Provider value={{ n: def.id, total: defs.length, section: def.section }}>
      {def.body}
    </PageContext.Provider>
  ),
}));
